"use client";

import {
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

/**
 * Hand-over between the two places that can provide Clerk in the browser:
 * a route group's `ClerkScope` (components/auth/clerk-scope.tsx) and the root
 * layout's signed-in upkeep (components/auth/signed-in-clerk-upkeep.tsx).
 *
 * @clerk/clerk-react allows ONE mounted `<ClerkProvider>` at a time: each
 * instance counts itself in a passive effect and throws "You've added
 * multiple <ClerkProvider> components" when another is already mounted.
 * The two providers here are siblings (the upkeep sits beside the page in the
 * root layout), so on a client-side navigation from a Clerk-free page into a
 * Clerk-scoped route group (marketing → "Log in" → /login) they must never
 * overlap, not even for one commit:
 *
 *   - `ClerkScopeGate` registers the scope in a layout effect, which makes
 *     the upkeep stand down (unmount its provider), and renders the scope's
 *     own provider only once the upkeep's provider has gone. On a document
 *     load the gate renders its children on the server and during hydration.
 *     A fresh client mount first reserves
 *     its scope before rendering a provider, even when the upkeep's delayed
 *     import resolves in that same commit.
 *   - The upkeep mounts its provider only while no scope is registered.
 *
 * React flushes the unmounting provider's passive cleanup before it renders
 * the update a layout effect scheduled, so the next provider's count check
 * always sees zero.
 *
 * No Clerk import: this module ships on every page.
 */
const mountedScopes = new Set<symbol>();
let upkeepProviderMounted = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function subscribeToClerkProviders(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function isClerkScopePresent(): boolean {
  return mountedScopes.size > 0;
}

function isUpkeepProviderMounted(): boolean {
  return upkeepProviderMounted;
}

// Server and hydration render: the upkeep never mounts a provider before
// hydration has finished, so a scope always renders its children there.
const serverUpkeepProviderMounted = () => false;
const serverScopeRegistered = () => true;

/** Called by the upkeep's provider component while it is mounted. */
export function useUpkeepProviderRegistration(): void {
  useLayoutEffect(() => {
    upkeepProviderMounted = true;
    notify();
    return () => {
      upkeepProviderMounted = false;
      notify();
    };
  }, []);
}

/** Wraps every ClerkScope's provider; see the module comment. */
export function ClerkScopeGate({ children }: { children: ReactNode }) {
  const [scope] = useState(() => Symbol());
  const registered = useSyncExternalStore(
    subscribeToClerkProviders,
    () => mountedScopes.has(scope),
    serverScopeRegistered,
  );
  const upkeepMounted = useSyncExternalStore(
    subscribeToClerkProviders,
    isUpkeepProviderMounted,
    serverUpkeepProviderMounted,
  );
  useLayoutEffect(() => {
    mountedScopes.add(scope);
    notify();
    return () => {
      mountedScopes.delete(scope);
      notify();
    };
  }, [scope]);
  return registered && !upkeepMounted ? children : null;
}
